import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';

interface AiProcessingAnimationProps {
  type: 'summary' | 'content' | 'message';
  className?: string;
}

export default function AiProcessingAnimation({
  type,
  className = '',
}: AiProcessingAnimationProps) {
  const { t } = useTranslation();
  const [messageIndex, setMessageIndex] = useState(0);

  const messages = t(`ai.processing.${type}`, { returnObjects: true }) as string[];

  useEffect(() => {
    if (!messages || messages.length <= 1) return;

    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % messages.length);
    }, 4500);

    return () => clearInterval(interval);
  }, [messages]);

  const currentMessage = Array.isArray(messages) ? messages[messageIndex] : t('ai.generating');

  return (
    <div className={`flex flex-col items-center justify-center py-8 ${className}`}>
      {/* Animated dots/orbs */}
      <div className="flex items-center gap-2 mb-6">
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className="w-3 h-3 rounded-full bg-primary-500"
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.5, 1, 0.5],
            }}
            transition={{
              duration: 1,
              repeat: Infinity,
              delay: i * 0.2,
              ease: 'easeInOut',
            }}
          />
        ))}
      </div>

      {/* Animated message */}
      <div className="h-6 relative overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.p
            key={messageIndex}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="text-sm text-gray-500 dark:text-gray-400 text-center"
          >
            {currentMessage}
          </motion.p>
        </AnimatePresence>
      </div>

      {/* Progress bar */}
      <div className="w-48 h-1 bg-gray-200 dark:bg-gray-700 rounded-full mt-4 overflow-hidden">
        <motion.div
          className="h-full bg-gradient-to-r from-primary-400 via-primary-600 to-primary-400"
          animate={{
            x: ['-100%', '100%'],
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
          style={{ width: '50%' }}
        />
      </div>
    </div>
  );
}
